<script setup lang="ts">
// Funnel / conversion chart. Host sorts descending by `value` so
// stages always read top-to-bottom in flow order. Capped at 32 stages
// (validator-enforced).

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { FunnelChartSpec } from "../sbi/protocol";
import { CHART_COLORS, FONT_STACK_MONO, PALETTE } from "../theme.ts";
import { chartChrome, resolveCssVar, tooltipDefaults } from "./echarts-base";
import { NARROW } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: FunnelChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const sortedStages = computed(() => {
  const rows = props.spec.data as ReadonlyArray<Record<string, unknown>>;
  return [...rows]
    .map((r) => ({
      name: String(r[props.spec.category] ?? ""),
      value: Number(r[props.spec.value]),
    }))
    .filter((s) => Number.isFinite(s.value))
    .sort((a, b) => b.value - a.value);
});

const total = computed(
  () => sortedStages.value[0]?.value ?? 0,
);

// Stage stroke (between adjacent funnel wedges) + label colour.
// Canvas can't resolve `var(--…)`, so without the resolution step
// the label paints with an invalid colour (usually invisible) and
// the inter-stage stroke either disappears or paints black.
const stageStroke = computed(
  () =>
    resolveCssVar("var(--lucent-funnel-stage-stroke)") ??
    CHART_COLORS.funnelStageStroke,
);
const stageLabelColor = computed(
  () =>
    resolveCssVar("var(--lucent-funnel-stage-label-color)") ??
    CHART_COLORS.funnelStageLabel,
);

// ECharts' default funnel margins (80px each side, 60px top/bottom)
// leave a ~160px funnel at 320px with its outside labels clipped. Narrow
// charts drop the margins and put (truncated) labels inside the stages;
// wide charts keep the defaults, stated explicitly.
const layout = computed(() => {
  const w = ctx.value.width;
  if (w > 0 && w < NARROW) {
    return {
      left: 8,
      right: 8,
      top: 8,
      bottom: 8,
      minSize: "30%",
      label: { position: "inside" as const, width: Math.max(40, Math.floor(w * 0.5)), overflow: "truncate" as const },
      labelLine: { show: false },
    };
  }
  return { left: 80, right: 80, top: 60, bottom: 60, label: {}, labelLine: {} };
});

const option = computed(() => ({
  ...chartChrome(ctx.value),
  tooltip: {
    ...tooltipDefaults("item", ctx.value),
    formatter: (p: { name?: string; value?: number; percent?: number }) =>
      `${p.name ?? ""}: ${p.value ?? 0} (${(p.percent ?? 0).toFixed(1)}%)`,
  },
  legend: { show: false },
  series: [
    {
      type: "funnel" as const,
      sort: "descending" as const,
      gap: 2,
      left: layout.value.left,
      right: layout.value.right,
      top: layout.value.top,
      bottom: layout.value.bottom,
      ...("minSize" in layout.value ? { minSize: layout.value.minSize } : {}),
      labelLine: layout.value.labelLine,
      data: sortedStages.value.map((s, i) => ({
        ...s,
        itemStyle: {
          color: PALETTE[i % PALETTE.length],
          borderColor: stageStroke.value,
          borderWidth: 1,
        },
      })),
      label: {
        color: stageLabelColor.value,
        fontFamily: FONT_STACK_MONO,
        fontSize: 11,
        fontWeight: 600,
        ...layout.value.label,
      },
    },
  ],
}));

const height = computed(() =>
  resolveChartHeight("funnel", props.spec.height),
);

function onClick(p: { dataIndex?: number; data?: unknown }) {
  if (!props.spec.id || typeof p?.dataIndex !== "number") return;
  const stage = sortedStages.value[p.dataIndex];
  if (!stage) return;
  const t = total.value || 1;
  emit("click-target", props.spec.id, {
    category: stage.name,
    value: stage.value,
    percent: (stage.value / t) * 100,
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
